import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import log from 'app/home/general-components/Logging';

function useWebSocket(url) {
    const webSocketComponentId = useRef(undefined)

    const [isConnected, setConnected] = useState(false);
    const [socket, setSocket] = useState(null);

    const isReconnectionEnable = useRef(true);
    const setReconnectionEnable = useCallback((newValue) => {
        isReconnectionEnable.current = newValue;
    }, []);
    const reconnectionTimers = useRef([]);

    const reconnectInterval = useRef(1000);
    const setReconnectInterval = useCallback((newValue) => {
        reconnectInterval.current = newValue;
    }, []);

    const onOpen = useRef(() => { });
    const onClose = useRef(() => { });
    const onMessage = useRef(() => { });
    const onError = useRef(() => { });

    const setOnOpen = (f) => {
        onOpen.current = f;
    };
    const setOnClose = (f) => {
        onClose.current = f;
    };
    const setOnMessage = (f) => {
        onMessage.current = f;
    };
    const setOnError = (f) => {
        onError.current = f;
    };

    const _reconnect = useCallback(() => {
        for (let i = 0; i < reconnectionTimers.current.length; i++) {
            const t = reconnectionTimers.current[i];
            clearTimeout(t);
        }

        const newSocket = new WebSocket(url);
        const webSockerReconnectionId = uuidv4();

        newSocket.onopen = (event) => {
            log('* ws open *', webSocketComponentId.current, webSockerReconnectionId);
            setConnected(true);
            onOpen.current(event);
            reconnectInterval.current = 1000;
        };
        newSocket.onmessage = (event) => {
            onMessage.current(event);
        }
        newSocket.onerror = (event) => {
            log('* ws error *', webSocketComponentId.current, webSockerReconnectionId);
            onError.current(event);
            setConnected(false);
        }
        newSocket.onclose = (event) => {
            log('* ws close *', webSocketComponentId.current, webSockerReconnectionId);
            onClose.current(event);
            setConnected(false);
            if (isReconnectionEnable.current) {
                log("* ws schedule reconnection *", reconnectInterval.current);
                reconnectionTimers.current.push(setTimeout(() => {
                    log("** ws reconnection **");
                    reconnectInterval.current = Math.min(8000, reconnectInterval.current * 2);
                    _reconnect();
                }, reconnectInterval.current));
            }
        }
        setSocket(newSocket);
    }, [url]);

    useEffect(() => {
        if (url === undefined) {
            log("* ws skip connection (url is undefined) *");
            return;
        }

        if (webSocketComponentId.current === undefined) {
            webSocketComponentId.current = uuidv4();
            log('* ws init *', webSocketComponentId.current);
            _reconnect();
        }
    }, [_reconnect, url]);

    return {
        socket, isConnected,
        setReconnectionEnable, setReconnectInterval,
        setOnOpen, setOnMessage, setOnClose, setOnError
    }
}


export default useWebSocket;